import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SEO from "../components/SEO";

import { fetchContent } from "../content/content"; // your existing articles thunk
import { fetchMedia } from "../content/media";     // <-- videos thunk

import {
  htmlToStructuredText,
  smartExcerpt,
  formatDate,
} from "../utils/PageParser";

const BASE_URL = import.meta?.env?.VITE_SITE_URL || "https://werlocal.us";

export const CATEGORY_SEO = {
  politics: {
    title: "Politics explained simply for everyday people – We Are Local",
    description:
      "Fake News Without the Fake — just the stuff that actually happened (we promise)",
    keywords:
      "politics, government, elections, policy, democracy, leaders, U.S., USA, US, democrats,news, GOP,Trump,The white house",
  },
  games: {
    title: "Games and puzzles made simple for everyday fun – We Are Local",
    description:
      "Play and explore puzzle games like Memory Match, Sudoku, Wordly, Hangman, 2048, and more — challenges and fun for each day",
    keywords:
      "games, puzzles, sudoku, memory match, word games, brain teasers, entertainment",
  },
  lifestyle: {
    title: "Entertainment – We Are Local",
    description:
      "Quizzes, horoscopes, tarot, and community entertainment. Discover daily insights, trivia, and cultural highlights.",
    keywords:
      "entertainment, horoscope, tarot, quizzes, trivia, culture, fun, daily guidance",
  },
  business: {
    title: "Business news made simple: updates that affect your money, work, and local community – We Are Local",
    description:
      "Business news made simple — just what you need to know",
    keywords:
      "Business headlines, market trends, local company, business, economics, finance, market,trends",
  },
  travel: {
    title: "Travel made easy: practical advice and updates for everyday travelers – We Are Local",
    description:
      "Travel made easy: practical advice and updates for everyday travelers",
    keywords: "trabel, internal teavel, usa, international travel",
  },
  humor: {
    title: "Everyday humor and funny stories to make you smile – We Are Local",
    description:
      "Everyday humor and funny stories to make you smile",
    keywords: "Lighthearted stories,local oddities,humor,fun,relax",
  },
  education: {
    title: "Education news made simple — what families and communities need to know – We Are Local",
    description:
      "Education news explained for families and communities — updates on schools, policies, and learning resources that directly impact everyday life",
    keywords:
      "education news,education policies,local school updates,school district news,university updates,higher education news,local education resources stories,local oddities,humor,fun,relax",
  },
  wellbeing: {
    title: "Wellbeing simplified: psychology, health, and everyday happiness – We Are Local",
    description:
      "Practical wellbeing insights: psychology, health, and everyday happiness explained in plain language",
    keywords:
      "wellbeing,wellness,mental health,happy life,psychology,healthy living,lifestyle balance,self care",
  },
  kids: {
    title: "Fun and learning for kids: activities, stories, and updates that families love – We Are Local",
    description:
      "Kids’ corner: fun activities, stories, and resources that support learning and family life",
    keywords:
      "kids activities,children learning,family fun,educational games,local kids events,stories for kids,parenting resources",
  },
  pets:{
    title: "Pets and companions: tips, stories, and local resources for happy animals – We Are Local",
    description:
      "Helpful advice and fun stories about pets — from care tips to local pet-friendly activities and resources",
    keywords:
      "pets,pet care,animal health,pet stories,dogs,cats,local pet events,pet-friendly places,animal wellness",
  },
  home: {
  title: "Home and living: ideas, resources, and stories for a better everyday life – We Are Local",
  description:
    "Inspiration, tips, and practical advice for home, family, design, and local living to make everyday life easier and more enjoyable",
  keywords:
    "home,living,household tips,interior design,DIY projects,local home events,family living,home improvement",
},
sports: {
  title: "Sports news simplified – We Are Local",
  description:
    "From championship highlights to local games — clear updates on the sports that move us",
  keywords:
    "sports, football, soccer, basketball, baseball, tennis, athletics, fitness, olympics, world cup, local sports, major leagues, athletes, competition, training, health, sport news"
},

finance: {
  title: "Finance news made simple – We Are Local",
  description:
    "Money, markets, and your wallet — explained without the jargon so you can stay in control",
  keywords:
    "finance, stock market, economy, investing, personal finance, banking, Wall Street, U.S. economy, business news, inflation, interest rates, financial planning, savings, retirement, markets, money management"
},
technology: {
  title: "Technology explained for everyday life – We Are Local",
  description:
    "From AI and cloud computing to gadgets and digital trends — we make technology simple, practical, and relevant to your world",
  keywords:
    "technology, tech news, innovation, AI, artificial intelligence, cloud computing, digital transformation, software, hardware, gadgets, startups, cybersecurity, internet, mobile, apps, future tech, IT trends"
}
};

const CATEGORY_BLURB = {
  politics:
    "Fake News Without the Fake — just the stuff that actually happened (we promise)",
  business:
    "Business news made simple — just what you need to know",
  education:
    "Education news made simple — what families and communities need to know",
  games:
    "Fun and easy games, puzzles, and brain teasers anyone can enjoy — explained simply so you can dive right in, no expertise needed",
  travel:
    "Travel tips and destination updates made simple — from local getaways to big trips, explained for everyday travelers who just want to know what matters",
  lifestyle:
    "Entertainment, quizzes, and daily insights explained in plain language — cultural highlights and fun activities for everyday life",
  humor:
    "Lighthearted humor, odd stories, and everyday laughs — explained simply so everyone can relax and smile, no background needed",
  wellbeing:
    "Practical wellbeing insights: psychology, health, and everyday happiness explained in plain language",  
  kids:
   "Kids’ corner: fun activities, stories, and resources that support learning and family life" ,
   pets: 
   "Helpful advice and fun stories about pets — from care tips to local pet-friendly activities and resources",
   home:
   "Inspiration, tips, and practical advice for home, family, design, and local living to make everyday life easier and more enjoyable",
  technology:
    "From AI breakthroughs to everyday gadgets — clear updates on the tech shaping our world",
  finance:
    "Money, markets, and your wallet — explained without the jargon so you can stay in control",  
  sports:
    "From championship highlights to local games — clear updates on the sports that move us",

};

const CATEGORY_OG = {
  politics: "https://werlocalbucket.s3.us-west-2.amazonaws.com/politics.png",
  business: "https://werlocalbucket.s3.us-west-2.amazonaws.com/business.png",
  education: "https://werlocalbucket.s3.us-west-2.amazonaws.com/education.png",
  games: "https://werlocalbucket.s3.us-west-2.amazonaws.com/games.png",
  travel: "https://werlocalbucket.s3.us-west-2.amazonaws.com/travel.png",
  lifestyle: "https://werlocalbucket.s3.us-west-2.amazonaws.com/lifestyle.png",
  humor: "https://werlocalbucket.s3.us-west-2.amazonaws.com/humor.jpg",
  pets: "https://werlocalbucket.s3.us-west-2.amazonaws.com/pets.png",
  wellbeing: "https://werlocalbucket.s3.us-west-2.amazonaws.com/wellbeing.png",
  kids: "https://werlocalbucket.s3.us-west-2.amazonaws.com/kids.png",
  home: "https://werlocalbucket.s3.us-west-2.amazonaws.com/home.jpg",
  technology: "https://werlocalbucket.s3.us-west-2.amazonaws.com/technology.jpg",
  finance: "https://werlocalbucket.s3.us-west-2.amazonaws.com/finance.jpg",
  sports: "https://werlocalbucket.s3.us-west-2.amazonaws.com/sport.jpg"
};

const trimMeta = (s = "", max = 160) => {
  const t = String(s).replace(/\s+/g, " ").trim();
  return t.length > max ? t.slice(0, max - 1) + "…" : t;
};

const MAX_VIDEOS = 5;

export default function CategoryPage() {
  const { categoryId: paramCategoryId } = useParams();

  // keep your location-derived default category behavior
  const { city, county, state, label } =
    useSelector((s) => s.local?.location) || {};
  const display = city || county || state || label || "California";
  const categoryId = (paramCategoryId ?? display)?.toLowerCase();

  const dispatch = useDispatch();

  // -------- ARTICLES (unchanged): state.content --------
  const contentState = useSelector((s) => s.content) || {};
  const items = useSelector((s) => s.content?.byCategory?.[categoryId] || []);
  const contentStatus = contentState.status;
  const contentError = contentState.error;

  useEffect(() => {
    if (categoryId) dispatch(fetchContent({ type: categoryId, tags: "" }));
  }, [dispatch, categoryId]);

  const normalizeTitle = (a) =>
    htmlToStructuredText(a.topic || a.title || a.name || "");
  const normalizeExcerpt = (a) =>
    smartExcerpt(
      htmlToStructuredText(a.page || a.description || a.link || ""),
      180
    );

  const filteredArticles = useMemo(() => {
    const c = (categoryId || "").toLowerCase();
    return (items || []).filter((a) => {
      const cat = (a.type || a.topic || "").toLowerCase();
      const tags = String(a.tags || "").toLowerCase();
      return cat === c || tags.includes(c);
    });
  }, [items, categoryId]);

  // -------- VIDEOS: state.media via fetchMedia --------
  // Assumes you mounted media reducer under `state.media`
  const mediaItems = useSelector(
    (s) => s.media?.byCategory?.[categoryId] || []
  );

  const mediaStatus = useSelector((s) => s.media?.status);
  const mediaError = useSelector((s) => s.media?.error);

  useEffect(() => {
    if (categoryId) dispatch(fetchMedia({ type: categoryId, tags: "" }));
  }, [dispatch, categoryId]);

  const videoItems = useMemo(() => {
    return (mediaItems || [])
      .filter((v) => typeof v.video === "string" && v.video.trim())
      .sort((a, b) => {
        const o = (b.orderby ?? 0) - (a.orderby ?? 0);
        if (o !== 0) return o;
        return new Date(b.dt || 0) - new Date(a.dt || 0);
      })
      .slice(0, MAX_VIDEOS);
  }, [mediaItems]);

  // ===== SEO =====
  const seo = CATEGORY_SEO[categoryId] || {
    title: `${categoryId} – We Are Local`,
    description: `Latest ${categoryId} news, stories, and community highlights.`,
    keywords: `${categoryId}, local news, community, We Are Local`,
  };

  const prettyCategory = categoryId?.replace(/-/g, " ") || "News";
  const canonical = `${BASE_URL}/category/${encodeURIComponent(
    categoryId || "news"
  )}`;
  const baseDesc =
    CATEGORY_BLURB[categoryId] ||
    "Latest local news, analysis, and stories from your community.";
  const ogImage = CATEGORY_OG[categoryId] || `${BASE_URL}/og/default.png`;

  const top = filteredArticles[0];
  const exampleTitle = top ? normalizeTitle(top) : "";
  const jsonLdCollection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: prettyCategory.charAt(0).toUpperCase() + prettyCategory.slice(1),
    url: canonical,
    description: baseDesc + (exampleTitle ? ` Example: ${exampleTitle}` : ""),
    isPartOf: { "@type": "WebSite", name: "We Are Local", url: BASE_URL },
    publisher: {
      "@type": "Organization",
      name: "We Are Local",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/logo.png` },
    },
  };

  // ----- Loading/errors for articles take precedence on page-level -----
  if (contentStatus === "loading")
    return <div className="container mt-4">Loading…</div>;
  if (contentError)
    return (
      <div className="container mt-4 text-danger">
        Error: {String(contentError)}
      </div>
    );

  // Split articles
  const leftNews = filteredArticles.slice(0, 5);
  const centerNews = filteredArticles.slice(5, 30);

  return (
    <div className="container-fluid py-3">
      {/* SEO */}
      <SEO
        title={seo.title}
        description={trimMeta(seo.description)}
        canonical={canonical}
        robots="index,follow"
        og={{
          url: canonical,
          title: seo.title,
          description: trimMeta(seo.description),
          type: "website",
          image: ogImage,
        }}
        twitter={{
          card: "summary_large_image",
          title: seo.title,
          description: trimMeta(seo.description),
          image: ogImage,
        }}
        jsonLd={jsonLdCollection}
      />

      {/* Page */}
      
       <div className="d-flex align-items-center gap-3 mb-2">
        {CATEGORY_OG[categoryId] && (
          <img
            src={CATEGORY_OG[categoryId]}
            alt={`${prettyCategory} category`}
            className="category-chip"
            loading="lazy"
            width="64"
            height="64"
          />
        )}
          <h1 className="mb-2 text-capitalize">{prettyCategory}</h1>
        </div>   
          <p className="site-tagline">{baseDesc}</p>

      <div className="row g-4">
        {/* Left: Pinned */}
        <div className="col-12 col-lg-3">
          <div className="section-title mb-2">Pinned</div>
          <ul className="headline-list">
            {leftNews.map((a, i) => (
              <li key={a.id || i}>
                <Link to={`/article/${a.id || a._id}`} className="hl-link">
                  {normalizeTitle(a)}
                </Link>
                <div className="hl-meta">{formatDate(a.dt)}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* Center: Articles */}
        <div className="col-12 col-lg-6">
          <div className="section-title mb-2">Articles</div>
          <ul className="headline-list big">
            {centerNews.map((a, i) => (
              <li key={a.id || i}>
                <Link to={`/article/${a.id || a._id}`} className="hl-link">
                  {normalizeTitle(a)}
                </Link>
                <div className="hl-excerpt">{normalizeExcerpt(a)}</div>
                <div className="hl-meta">{formatDate(a.dt)}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: Interesting (videos via Redux fetchMedia; fallback if none) */}
        <div className="col-12 col-lg-3">
          <div className="section-title mb-2">Interesting</div>

          {mediaStatus === "loading" && (
            <div className="text-muted mb-2">Loading videos…</div>
          )}
          {mediaError && (
            <div className="text-danger mb-2">Video error: {mediaError}</div>
          )}

          {videoItems.length > 0 ? (
            videoItems.map((v, i) => (
              <div
                key={v.id || i}
                className="ratio ratio-16x9 mb-3"
                // trusted iframe HTML from your DB
                dangerouslySetInnerHTML={{ __html: v.video }}
              />
            ))
          ) : (
            <>
              <div className="ratio ratio-16x9 mb-3">
                <iframe
                  src="https://www.youtube.com/embed/OiClkYNtTc0?si=whiixOc6yUIpsPhH"
                  title="YouTube video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
              <div className="ratio ratio-16x9 mb-3">
                <iframe
                  src="https://www.youtube.com/embed/rKZJJIPFu7U?si=PW2lyTyw9Kmgx_U9"
                  title="YouTube video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
              <a className="ad-tile mb-3 d-block" href="#">
                <span>Ad Banner</span>
                <div className="ratio ratio-16x9">
                  <iframe
                    src="https://www.youtube.com/embed/Vjx1mNOq0kg?si=9Ae97W3_-dWmf2TD"
                    title="YouTube video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </a>
              <a className="ad-tile d-block" href="#">
                <span>Promo</span>
                <div className="ratio ratio-16x9">
                  <iframe
                    src="https://www.youtube.com/embed/63-yN641Zn4?si=wjgKlRWW_HAm0Akz"
                    title="YouTube video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}